import type { AutoReplyLang, LangText } from './whatsappAutoReplyFlows';

/**
 * Meta message templates for the lead journey (business-initiated messages,
 * sent outside the 24h customer service window). Drafted in EN / GU / HI and
 * submitted to Meta from the WhatsApp hub. Positional variables {{1}}..{{n}}.
 *
 * Meta limits: body 1024, header text 60, footer 60, quick-reply button 25.
 * A body must not start or end with a variable.
 */

export type TemplateCategory = 'UTILITY' | 'MARKETING';

export interface TemplateVariable {
  label: string;
  example: string;
}

export interface TemplateDraft {
  name: string;
  /** Increment when built-in wording changes enough to replace saved legacy edits. */
  draftRevision?: number;
  category: TemplateCategory;
  /** Journey automation that sends it. */
  automation: string;
  purpose: string;
  /** LOCATION header: the site pin is attached when sending. */
  header?: { type: 'LOCATION' } | { type: 'TEXT'; text: LangText } | { type: 'DOCUMENT'; referenceTemplate: string };
  body: LangText;
  variables: TemplateVariable[];
  footer?: LangText;
  buttons?: LangText[];
  /** The first button opens a dynamic URL; its final parameter is supplied when sending. */
  urlButton?: { template: string; sample: string; note: string };
}

export const TEMPLATE_LANG_CODES: Record<AutoReplyLang, string> = { en: 'en', gu: 'gu', hi: 'hi' };
export const VISIT_BROCHURE_URL_TEMPLATE = 'https://www.swarnagroup.in/projects/{{1}}';
export const VISIT_BROCHURE_SAMPLE_URL = 'https://www.swarnagroup.in/projects/swarna-avsar';

const FOOTER_UTILITY: LangText = { en: 'Swarna Group', gu: 'સ્વર્ણ ગ્રુપ', hi: 'स्वर्ण ग्रुप' };
const FOOTER_MARKETING: LangText = {
  en: 'Swarna Group · Tap Stop updates to opt out',
  gu: 'સ્વર્ણ ગ્રુપ · બંધ કરવા Stop updates દબાવો',
  hi: 'स्वर्ण ग्रुप · बंद करने के लिए Stop updates दबाएं',
};
const BTN_STOP: LangText = { en: 'Stop updates', gu: 'Stop updates', hi: 'Stop updates' };
const BTN_RESCHEDULE: LangText = { en: 'Reschedule', gu: 'સમય બદલો', hi: 'समय बदलें' };
const BTN_VISIT: LangText = { en: 'Book site visit', gu: 'સાઇટ વિઝિટ બુક કરો', hi: 'साइट विज़िट बुक करें' };
const BTN_CALL: LangText = { en: 'Call me', gu: 'મને કૉલ કરો', hi: 'मुझे कॉल करें' };
const BTN_BROCHURE: LangText = { en: 'Send brochure', gu: 'બ્રોશર મોકલો', hi: 'ब्रोशर भेजें' };
const BTN_EXPLORE: LangText = { en: 'Explore projects', gu: 'પ્રોજેક્ટ જુઓ', hi: 'प्रोजेक्ट देखें' };

const v = (label: string, example: string): TemplateVariable => ({ label, example });

export const TEMPLATE_DRAFTS: TemplateDraft[] = [
  {
    name: 'swarna_broker_profile_invite',
    draftRevision: 2,
    category: 'UTILITY',
    automation: 'brokerProfileInvite',
    purpose: 'Staff sends a one-use profile form after a broker-only visit.',
    body: {
      en: 'Hello {{1}}, please complete your Swarna Group broker profile using the button below.\n\nThis private form expires in 48 hours and works once.',
      gu: 'નમસ્તે {{1}}, કૃપા કરીને નીચેના બટનથી તમારી સ્વર્ણ ગ્રુપ બ્રોકર પ્રોફાઇલ પૂર્ણ કરો.\n\nઆ ખાનગી ફોર્મ 48 કલાકમાં સમાપ્ત થાય છે અને એક જ વાર કામ કરે છે.',
      hi: 'नमस्ते {{1}}, कृपया नीचे दिए बटन से अपनी स्वर्ण ग्रुप ब्रोकर प्रोफ़ाइल पूरी करें।\n\nयह निजी फ़ॉर्म 48 घंटे में समाप्त हो जाएगा और केवल एक बार काम करेगा।',
    },
    variables: [v('Broker name', 'Amit')],
    footer: FOOTER_UTILITY,
    buttons: [{ en: 'Complete profile', gu: 'પ્રોફાઇલ પૂર્ણ કરો', hi: 'प्रोफ़ाइल पूरी करें' }],
    urlButton: {
      template: 'https://crm.swarnagroup.in/broker-profile/{{1}}',
      sample: 'https://crm.swarnagroup.in/broker-profile/example-token',
      note: 'The sent button opens this broker’s private one-use form.',
    },
  },
  {
    name: 'swarna_buyer_form_link',
    draftRevision: 1,
    category: 'UTILITY',
    automation: 'buyerFormLink',
    purpose: 'Office sends the private buyer details form for a booking. No price, only the flat and the link.',
    body: {
      en: 'Hello {{1}},\n\nPlease fill in the buyer details for your booking.\n\n*Flat:* {{2}}\n*Project:* {{3}}\n\nYou can check how your details will be written in Gujarati before sending.\n\n_This private link works for 7 days._\n\nNeed help? Call +91 63587 92762.',
      gu: 'નમસ્તે {{1}},\n\nકૃપા કરીને તમારા બુકિંગ માટે ખરીદનારની વિગતો ભરો.\n\n*ફ્લેટ:* {{2}}\n*પ્રોજેક્ટ:* {{3}}\n\nમોકલતા પહેલાં તમારી વિગતો ગુજરાતીમાં કેવી લખાશે તે જોઈ શકશો.\n\n_આ ખાનગી લિંક ૭ દિવસ ચાલશે._\n\nમદદ માટે કૉલ કરો: +91 63587 92762.',
      hi: 'नमस्ते {{1}},\n\nकृपया अपनी बुकिंग के लिए खरीदार का विवरण भरें।\n\n*फ्लैट:* {{2}}\n*प्रोजेक्ट:* {{3}}\n\nभेजने से पहले आप देख सकते हैं कि आपका विवरण गुजराती में कैसे लिखा जाएगा।\n\n_यह निजी लिंक 7 दिन तक चलेगा।_\n\nमदद के लिए कॉल करें: +91 63587 92762.',
    },
    variables: [v('Buyer name, or there', 'Chetan Patel'), v('Flat', 'C-101, C Wing'), v('Project', 'Swarna Avsar')],
    footer: FOOTER_UTILITY,
    buttons: [{ en: 'Fill details', gu: 'વિગતો ભરો', hi: 'विवरण भरें' }],
    urlButton: {
      template: 'https://crm.swarnagroup.in/buyer-form/{{1}}',
      sample: 'https://crm.swarnagroup.in/buyer-form/example-token',
      note: 'The sent button opens this booking’s private buyer details form.',
    },
  },
  // ── 1 New enquiry ───────────────────────────────────────────────
  {
    name: 'swarna_enquiry_received',
    draftRevision: 2,
    category: 'UTILITY',
    automation: 'enquiryWelcome',
    purpose: 'Welcome a new lead whose project is known in CRM.',
    body: {
      en: 'Hi {{1}} 👋\n\nWe’ve received your enquiry for *{{2}}* by *Swarna Group*.\n\nOur team will be in touch to answer your questions. You can request the brochure, plan a site visit, or explore our other projects below.',
      gu: 'નમસ્તે {{1}} 👋\n\n*સ્વર્ણ ગ્રુપ*ના *{{2}}* પ્રોજેક્ટ માટે તમારી પૂછપરછ અમને મળી ગઈ છે.\n\nતમારા પ્રશ્નોના જવાબ આપવા અમારી ટીમ તમારો સંપર્ક કરશે. તમે નીચે બ્રોશર મંગાવી શકો છો, સાઇટ વિઝિટ ગોઠવી શકો છો અથવા અમારા અન્ય પ્રોજેક્ટ જોઈ શકો છો.',
      hi: 'नमस्ते {{1}} 👋\n\n*स्वर्ण ग्रुप* के *{{2}}* प्रोजेक्ट के लिए आपकी पूछताछ हमें मिल गई है।\n\nआपके सवालों का जवाब देने के लिए हमारी टीम आपसे संपर्क करेगी। आप नीचे ब्रोशर माँग सकते हैं, साइट विज़िट तय कर सकते हैं या हमारे अन्य प्रोजेक्ट देख सकते हैं।',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar')],
    footer: FOOTER_UTILITY,
    buttons: [BTN_BROCHURE, BTN_VISIT, BTN_EXPLORE],
  },
  {
    name: 'swarna_enquiry_choose_project',
    draftRevision: 1,
    category: 'UTILITY',
    automation: 'enquiryWelcome',
    purpose: 'Welcome a new lead whose project is not yet known in CRM.',
    body: {
      en: 'Hi {{1}} 👋\n\nWe’ve received your enquiry with *Swarna Group*.\n\nOur team will be in touch to answer your questions. Choose a project below to see its possession timeline and request more details.',
      gu: 'નમસ્તે {{1}} 👋\n\n*સ્વર્ણ ગ્રુપ* પાસે તમારી પૂછપરછ અમને મળી ગઈ છે.\n\nતમારા પ્રશ્નોના જવાબ આપવા અમારી ટીમ તમારો સંપર્ક કરશે. પઝેશન સમયરેખા જોવા અને વધુ માહિતી મેળવવા નીચે પ્રોજેક્ટ પસંદ કરો.',
      hi: 'नमस्ते {{1}} 👋\n\n*स्वर्ण ग्रुप* को आपकी पूछताछ मिल गई है।\n\nआपके सवालों का जवाब देने के लिए हमारी टीम आपसे संपर्क करेगी। पज़ेशन की समयसीमा देखने और अधिक जानकारी पाने के लिए नीचे एक प्रोजेक्ट चुनें।',
    },
    variables: [v('Customer name', 'Amit')],
    footer: FOOTER_UTILITY,
    buttons: [{ en: 'Select project', gu: 'પ્રોજેક્ટ પસંદ કરો', hi: 'प्रोजेक्ट चुनें' }],
  },
  {
    name: 'swarna_call_attempt',
    category: 'UTILITY',
    automation: 'missedCall',
    purpose: 'Executive logged a call as CNR or busy.',
    body: {
      en: 'Hi {{1}}, {{2}} from Swarna Group tried to call you about {{3}}. When would be a good time to talk?',
      gu: 'નમસ્તે {{1}}, સ્વર્ણ ગ્રુપમાંથી {{2}} એ {{3}} વિશે તમને કૉલ કર્યો હતો. વાત કરવા માટે કયો સમય અનુકૂળ રહેશે?',
      hi: 'नमस्ते {{1}}, स्वर्ण ग्रुप से {{2}} ने {{3}} के बारे में आपको कॉल किया था। बात करने के लिए कौन सा समय ठीक रहेगा?',
    },
    variables: [v('Customer name', 'Amit'), v('Executive', 'Rahul'), v('Project', 'Swarna Avsar')],
    footer: FOOTER_UTILITY,
    buttons: [
      { en: 'Call me now', gu: 'હમણાં કૉલ કરો', hi: 'अभी कॉल करें' },
      { en: 'Call me later', gu: 'પછી કૉલ કરો', hi: 'बाद में कॉल करें' },
      { en: 'Chat on WhatsApp', gu: 'WhatsApp પર વાત', hi: 'WhatsApp पर बात' },
    ],
  },

  // ── 2 Follow-up ─────────────────────────────────────────────────
  {
    // Current no-reply nudge: project, homes, area and possession from the CMS.
    // Replaces swarna_enquiry_followup (3 placeholders). Never show prices.
    name: 'swarna_enquiry_nudge',
    category: 'MARKETING',
    automation: 'noReplyNudge',
    purpose: 'One nudge when a new enquiry has not replied or been reached in 24 hours.',
    body: {
      en: 'Hi {{1}}, did you get a chance to look at *{{2}}*? 🏡\n\n✅ {{3}}\n📍 {{4}}\n🔑 {{5}}\n\nFor the latest pricing and availability, we’d be happy to show you the available options during a site visit.\n\nWould you like to see the site this week?',
      gu: 'નમસ્તે {{1}}, શું તમે *{{2}}* જોઈ શક્યા? 🏡\n\n✅ {{3}}\n📍 {{4}}\n🔑 {{5}}\n\nતાજા ભાવ અને ઉપલબ્ધતા માટે, સાઇટ વિઝિટ દરમિયાન અમે તમને ઉપલબ્ધ વિકલ્પો બતાવીશું.\n\nશું તમે આ અઠવાડિયે સાઇટ જોવા માંગો છો?',
      hi: 'नमस्ते {{1}}, क्या आप *{{2}}* देख पाए? 🏡\n\n✅ {{3}}\n📍 {{4}}\n🔑 {{5}}\n\nनई कीमत और उपलब्धता के लिए, साइट विज़िट के दौरान हम आपको उपलब्ध विकल्प दिखाएँगे।\n\nक्या आप इस हफ़्ते साइट देखना चाहेंगे?',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar'), v('Homes', '2 BHK homes & shops'), v('Area', 'Vanakla, Surat'), v('Possession', 'Possession: December 2026')],
    footer: FOOTER_MARKETING,
    buttons: [BTN_VISIT, BTN_BROCHURE, BTN_STOP],
  },
  {
    // Older nudge (3 placeholders), replaced by swarna_enquiry_nudge.
    name: 'swarna_enquiry_followup',
    category: 'MARKETING',
    automation: 'noReplyNudge',
    purpose: 'One nudge when a new enquiry has not replied or been reached in 24 hours.',
    body: {
      en: 'Hi {{1}}, did you get a chance to look at *{{2}}*? {{3}} homes are available.\n\nFor the latest pricing and availability, we’d be happy to show you the available options during a site visit.\n\nWould you like to see the site this week?',
      gu: 'નમસ્તે {{1}}, શું તમે *{{2}}* જોઈ શક્યા? {{3}} ઘર ઉપલબ્ધ છે.\n\nતાજા ભાવ અને ઉપલબ્ધતા માટે, સાઇટ વિઝિટ દરમિયાન અમે તમને ઉપલબ્ધ વિકલ્પો બતાવીશું.\n\nશું તમે આ અઠવાડિયે સાઇટ જોવા માંગો છો?',
      hi: 'नमस्ते {{1}}, क्या आप *{{2}}* देख पाए? {{3}} घर उपलब्ध हैं।\n\nनई कीमत और उपलब्धता के लिए, साइट विज़िट के दौरान हम आपको उपलब्ध विकल्प दिखाएँगे।\n\nक्या आप इस हफ़्ते साइट देखना चाहेंगे?',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar'), v('Configurations', '2 & 3 BHK')],
    footer: FOOTER_MARKETING,
    buttons: [BTN_VISIT, BTN_BROCHURE, BTN_STOP],
  },
  {
    name: 'swarna_cold_lead',
    category: 'MARKETING',
    automation: 'coldLead',
    purpose: 'Soft check-in for a lead with no activity for 7 days.',
    body: {
      en: 'Hi {{1}}, still looking for a home in {{2}}? *{{3}}* has a few {{4}} homes left.\n\nFor the latest pricing and availability, we’d be happy to show you the available options during a site visit.',
      gu: 'નમસ્તે {{1}}, શું તમે હજી {{2}} માં ઘર શોધી રહ્યા છો? *{{3}}* માં થોડા જ {{4}} ઘર બાકી છે.\n\nતાજા ભાવ અને ઉપલબ્ધતા માટે, સાઇટ વિઝિટ દરમિયાન અમે તમને ઉપલબ્ધ વિકલ્પો બતાવીશું.',
      hi: 'नमस्ते {{1}}, क्या आप अभी भी {{2}} में घर ढूँढ रहे हैं? *{{3}}* में कुछ ही {{4}} घर बचे हैं।\n\nनई कीमत और उपलब्धता के लिए, साइट विज़िट के दौरान हम आपको उपलब्ध विकल्प दिखाएँगे।',
    },
    variables: [v('Customer name', 'Amit'), v('Area', 'Vanakla, Surat'), v('Project', 'Swarna Avsar'), v('Configuration', '2 BHK')],
    footer: FOOTER_MARKETING,
    // First button sends the brochure (payload ar:brochure).
    buttons: [BTN_BROCHURE, BTN_VISIT, BTN_STOP],
  },

  // ── 3 Site visit ────────────────────────────────────────────────
  {
    name: 'swarna_visit_confirmed',
    category: 'UTILITY',
    automation: 'visitConfirm',
    purpose: 'Sent when a site visit is scheduled in the CRM.',
    body: {
      en: 'Hi {{1}}, your site visit to {{2}} is confirmed for {{3}}.\nAddress: {{4}}\n{{5}} will meet you at the site. Tap below if you need to change the time.',
      gu: 'નમસ્તે {{1}}, {{2}} ની તમારી સાઇટ વિઝિટ {{3}} માટે કન્ફર્મ છે.\nસરનામું: {{4}}\n{{5}} તમને સાઇટ પર મળશે. સમય બદલવો હોય તો નીચે ટેપ કરો.',
      hi: 'नमस्ते {{1}}, {{2}} की आपकी साइट विज़िट {{3}} के लिए कन्फर्म है।\nपता: {{4}}\n{{5}} आपसे साइट पर मिलेंगे। समय बदलना हो तो नीचे टैप करें।',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar'), v('Date and time', 'Sat 18 Oct, 11:00 AM'), v('Site address', 'Vanakla, Palanpur Canal Road, Surat'), v('Executive', 'Rahul')],
    footer: FOOTER_UTILITY,
    buttons: [BTN_RESCHEDULE, { en: 'Cancel visit', gu: 'વિઝિટ રદ કરો', hi: 'विज़िट रद्द करें' }],
  },
  {
    name: 'swarna_visit_reminder_day',
    category: 'UTILITY',
    automation: 'visitReminders',
    purpose: 'Reminder the day before the visit.',
    body: {
      en: 'Hi {{1}}, a quick reminder: your visit to {{2}} is tomorrow at {{3}}. See you there!',
      gu: 'નમસ્તે {{1}}, યાદ અપાવીએ: {{2}} ની તમારી વિઝિટ આવતીકાલે {{3}} વાગ્યે છે. ત્યાં મળીએ!',
      hi: 'नमस्ते {{1}}, याद दिला दें: {{2}} की आपकी विज़िट कल {{3}} बजे है। वहीं मिलते हैं!',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar'), v('Time', '11:00 AM')],
    footer: FOOTER_UTILITY,
    buttons: [{ en: "I'll be there", gu: 'હું આવીશ', hi: 'मैं आऊँगा' }, BTN_RESCHEDULE],
  },
  {
    name: 'swarna_visit_reminder_soon',
    category: 'UTILITY',
    automation: 'visitReminders',
    purpose: 'Reminder 2 hours before, with the site pin as the header.',
    header: { type: 'LOCATION' },
    body: {
      en: 'Hi {{1}}, see you in about 2 hours at {{2}}. Tap the map above for directions. {{3}} will be waiting for you.',
      gu: 'નમસ્તે {{1}}, લગભગ 2 કલાકમાં {{2}} પર મળીએ. રસ્તો જોવા ઉપરના નકશા પર ટેપ કરો. {{3}} તમારી રાહ જોશે.',
      hi: 'नमस्ते {{1}}, लगभग 2 घंटे में {{2}} पर मिलते हैं। रास्ते के लिए ऊपर के नक्शे पर टैप करें। {{3}} आपका इंतज़ार करेंगे।',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar'), v('Executive', 'Rahul')],
    footer: FOOTER_UTILITY,
    buttons: [{ en: 'Running late', gu: 'થોડું મોડું થશે', hi: 'थोड़ी देर होगी' }, BTN_RESCHEDULE],
  },
  {
    name: 'swarna_visit_missed',
    category: 'UTILITY',
    automation: 'visitNoShow',
    purpose: 'Customer did not come for the scheduled visit.',
    body: {
      en: 'Hi {{1}}, we missed you at {{2}} today. No problem, would you like to pick another time for your visit?',
      gu: 'નમસ્તે {{1}}, આજે {{2}} પર અમે તમારી રાહ જોઈ. કોઈ વાંધો નહીં, શું તમે વિઝિટ માટે બીજો સમય પસંદ કરશો?',
      hi: 'नमस्ते {{1}}, आज {{2}} पर हमने आपका इंतज़ार किया। कोई बात नहीं, क्या आप विज़िट के लिए दूसरा समय चुनेंगे?',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar')],
    footer: FOOTER_UTILITY,
    buttons: [{ en: 'Pick a new time', gu: 'નવો સમય પસંદ કરો', hi: 'नया समय चुनें' }, BTN_CALL],
  },

  // ── 4 After visit ───────────────────────────────────────────────
  {
    name: 'swarna_manual_brochure',
    category: 'UTILITY',
    automation: 'manualBrochure',
    purpose: 'Staff shares the selected project brochure PDF from a lead chat.',
    header: { type: 'DOCUMENT', referenceTemplate: 'swarna_manual_brochure' },
    body: {
      en: 'Hello {{1}} 🙏\n\n*{{2}} brochure*\n\nPlease find the project brochure attached as a PDF. It has the project information for you to review at your convenience.\n\nIf you have a question or would like to arrange a site visit, reply here and our team will help.\n\n*Swarna Group*',
      gu: 'નમસ્તે {{1}} 🙏\n\n*{{2}} બ્રોશર*\n\nપ્રોજેક્ટનું બ્રોશર PDF તરીકે જોડ્યું છે. તમારી અનુકૂળતા મુજબ પ્રોજેક્ટની માહિતી જોઈ શકો છો.\n\nતમને કોઈ પ્રશ્ન હોય અથવા સાઇટ વિઝિટ ગોઠવવી હોય તો અહીં જવાબ આપો. અમારી ટીમ મદદ કરશે.\n\n*સ્વર્ણ ગ્રુપ*',
      hi: 'नमस्ते {{1}} 🙏\n\n*{{2}} ब्रोशर*\n\nप्रोजेक्ट का ब्रोशर PDF के रूप में संलग्न है। आप अपनी सुविधा से प्रोजेक्ट की जानकारी देख सकते हैं।\n\nकोई सवाल हो या साइट विज़िट तय करनी हो तो यहाँ जवाब दें। हमारी टीम मदद करेगी।\n\n*स्वर्ण ग्रुप*',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar')],
  },
  {
    name: 'swarna_manual_choose_project',
    category: 'UTILITY',
    automation: 'manualBrochure',
    purpose: 'Staff asks a lead without a selected project which brochure they want.',
    body: {
      en: 'Hello {{1}} 🙏\n\nWhich *Swarna Group* project brochure would you like to receive? Tap *Choose project* below and select a project. We will send its PDF brochure here.',
      gu: 'નમસ્તે {{1}} 🙏\n\nતમને *સ્વર્ણ ગ્રુપ*ના કયા પ્રોજેક્ટનું બ્રોશર જોઈએ છે? નીચે *પ્રોજેક્ટ પસંદ કરો* પર ટેપ કરી પ્રોજેક્ટ પસંદ કરો. અમે તેનું PDF બ્રોશર અહીં મોકલીશું.',
      hi: 'नमस्ते {{1}} 🙏\n\nआपको *स्वर्ण ग्रुप* के किस प्रोजेक्ट का ब्रोशर चाहिए? नीचे *प्रोजेक्ट चुनें* पर टैप करके प्रोजेक्ट चुनें। हम उसका PDF ब्रोशर यहीं भेजेंगे।',
    },
    variables: [v('Customer name', 'Amit')],
    buttons: [{ en: 'Choose project', gu: 'પ્રોજેક્ટ પસંદ કરો', hi: 'प्रोजेक्ट चुनें' }],
  },
  {
    name: 't2_site_visit_brochure',
    category: 'UTILITY',
    automation: 'visitBrochure',
    purpose: 'Immediately after a completed visit: attach the project brochure PDF and link to project details.',
    header: { type: 'DOCUMENT', referenceTemplate: 't2_site_visit_brochure' },
    body: {
      en: 'Hello *{{1}},* thank you for visiting *{{2}}.* 🙏\n\nWe hope your site visit was helpful.\n\nWe have attached the project *brochure* for your reference.\n\nYou can also view complete project details using the button below.\n\nIf you have any questions or would like to schedule a revisit, you can reply here or call us anytime.\n\n*Swarna Group*',
      gu: 'નમસ્તે *{{1}},* *{{2}}* ની મુલાકાત લેવા બદલ આભાર. 🙏\n\nઆશા છે કે તમારી સાઇટ વિઝિટ ઉપયોગી રહી હશે.\n\nતમારા માટે પ્રોજેક્ટનું *બ્રોશર* જોડ્યું છે.\n\nનીચેના બટનથી તમે પ્રોજેક્ટની સંપૂર્ણ માહિતી પણ જોઈ શકો છો.\n\nતમને કોઈ પ્રશ્ન હોય અથવા ફરી મુલાકાત ગોઠવવી હોય તો અહીં જવાબ આપો અથવા અમને કૉલ કરો.\n\n*સ્વર્ણ ગ્રુપ*',
      hi: 'नमस्ते *{{1}},* *{{2}}* की साइट विज़िट के लिए धन्यवाद। 🙏\n\nआशा है कि आपकी साइट विज़िट उपयोगी रही होगी।\n\nआपके लिए प्रोजेक्ट का *ब्रोशर* संलग्न किया है।\n\nनीचे दिए बटन से आप प्रोजेक्ट की पूरी जानकारी भी देख सकते हैं।\n\nकोई सवाल हो या फिर से विज़िट तय करनी हो तो यहाँ जवाब दें या हमें कॉल करें।\n\n*स्वर्ण ग्रुप*',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar')],
    buttons: [{ en: 'View More Details', gu: 'વધુ માહિતી જુઓ', hi: 'और जानकारी देखें' }],
    urlButton: {
      template: VISIT_BROCHURE_URL_TEMPLATE,
      sample: VISIT_BROCHURE_SAMPLE_URL,
      note: 'The sent button uses the visited project’s website slug.',
    },
  },
  {
    name: 'swarna_visit_thanks',
    category: 'UTILITY',
    automation: 'visitThanks',
    purpose: 'Older visit thank-you (Loved it / Need more details / Not for me). Replaced by swarna_visit_feedback + swarna_visit_followup; taps on messages already sent still work.',
    body: {
      en: 'Hi {{1}}, thank you for visiting {{2}} today. How was your visit?',
      gu: 'નમસ્તે {{1}}, આજે {{2}} ની મુલાકાત લેવા બદલ આભાર. તમારી વિઝિટ કેવી રહી?',
      hi: 'नमस्ते {{1}}, आज {{2}} आने के लिए धन्यवाद। आपकी विज़िट कैसी रही?',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar')],
    footer: FOOTER_UTILITY,
    buttons: [
      { en: 'Loved it', gu: 'ખૂબ ગમ્યું', hi: 'बहुत पसंद आया' },
      { en: 'Need more details', gu: 'વધુ માહિતી જોઈએ', hi: 'और जानकारी चाहिए' },
      { en: 'Not for me', gu: 'મારા માટે નથી', hi: 'मेरे लिए नहीं' },
    ],
  },
  {
    name: 'swarna_visit_feedback',
    category: 'UTILITY',
    automation: 'visitThanks',
    purpose: 'About 10 minutes after the presenter saves the visit: how was our service (not about buying).',
    body: {
      en: 'Hi {{1}}, thank you for visiting *{{2}}* today. 🙏\n\nWe would love to know: *how was your experience with our team?*\n\nYour feedback helps us serve every family better.',
      gu: 'નમસ્તે {{1}}, આજે *{{2}}* ની મુલાકાત લેવા બદલ આભાર. 🙏\n\nઅમને જણાવો: *અમારી ટીમ સાથે તમારો અનુભવ કેવો રહ્યો?*\n\nતમારો પ્રતિભાવ અમને દરેક પરિવારની વધુ સારી સેવા કરવામાં મદદ કરે છે.',
      hi: 'नमस्ते {{1}}, आज *{{2}}* आने के लिए धन्यवाद। 🙏\n\nहमें बताइए: *हमारी टीम के साथ आपका अनुभव कैसा रहा?*\n\nआपकी राय से हम हर परिवार की और बेहतर सेवा कर पाते हैं।',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar')],
    footer: FOOTER_UTILITY,
    buttons: [
      // Meta: no emojis or formatting in template buttons.
      { en: 'Excellent', gu: 'ઉત્તમ', hi: 'बहुत बढ़िया' },
      { en: 'Good', gu: 'સારો', hi: 'अच्छा' },
      { en: 'Could be better', gu: 'વધુ સારું થઈ શકે', hi: 'बेहतर हो सकता था' },
    ],
  },
  {
    name: 'swarna_visit_followup',
    category: 'UTILITY',
    automation: 'visitFollowup',
    purpose: 'Next day 11 AM after a visit, when the customer has not chatted in the last 24 hours (otherwise sent free in the chat).',
    body: {
      en: 'Hi {{1}}, thank you again for visiting *{{2}}* yesterday. 🏡\n\nIf you have any questions about *floor plans, payment plans or availability*, our team is here to help.\n\nPlease choose an option below.',
      gu: 'નમસ્તે {{1}}, ગઈકાલે *{{2}}* ની મુલાકાત લેવા બદલ ફરી આભાર. 🏡\n\nજો તમને *ફ્લોર પ્લાન, પેમેન્ટ પ્લાન કે ઉપલબ્ધતા* વિશે કોઈ પ્રશ્ન હોય, તો અમારી ટીમ મદદ માટે તૈયાર છે.\n\nકૃપા કરીને નીચેથી એક વિકલ્પ પસંદ કરો.',
      hi: 'नमस्ते {{1}}, कल *{{2}}* आने के लिए फिर से धन्यवाद। 🏡\n\nअगर आपके मन में *फ्लोर प्लान, पेमेंट प्लान या उपलब्धता* के बारे में कोई सवाल है, तो हमारी टीम मदद के लिए तैयार है।\n\nकृपया नीचे से एक विकल्प चुनें।',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar')],
    footer: FOOTER_UTILITY,
    buttons: [
      { en: 'Need more details', gu: 'વધુ માહિતી જોઈએ', hi: 'और जानकारी चाहिए' },
      { en: 'Not for me', gu: 'મારા માટે નથી', hi: 'मेरे लिए नहीं' },
    ],
  },
  {
    // Was swarna_price_details. Never show prices: unit only, price at the visit.
    name: 'swarna_unit_details',
    category: 'UTILITY',
    automation: 'priceDetails',
    purpose: 'Executive shares the unit discussed (no price: pricing is shown at a site visit).',
    body: {
      en: 'Hi {{1}}, as discussed, here are the details for *{{2}}*:\nUnit: {{3}}\n\nFor the latest pricing and availability, we’d be happy to show you the available options during a site visit.\n\nReply here and {{4}} will help you.',
      gu: 'નમસ્તે {{1}}, વાત થયા મુજબ *{{2}}* ની વિગતો:\nયુનિટ: {{3}}\n\nતાજા ભાવ અને ઉપલબ્ધતા માટે, સાઇટ વિઝિટ દરમિયાન અમે તમને ઉપલબ્ધ વિકલ્પો બતાવીશું.\n\n{{4}} સાથે વાત કરવા અહીં જવાબ આપો.',
      hi: 'नमस्ते {{1}}, बात के अनुसार *{{2}}* की जानकारी:\nयूनिट: {{3}}\n\nनई कीमत और उपलब्धता के लिए, साइट विज़िट के दौरान हम आपको उपलब्ध विकल्प दिखाएँगे।\n\n{{4}} से बात करने के लिए यहाँ जवाब दें।',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar'), v('Unit', '2 BHK, 7th floor, A-704'), v('Executive', 'Rahul')],
    footer: FOOTER_UTILITY,
    // Sent after a call too, so not "Visit again".
    buttons: [{ en: 'Schedule a visit', gu: 'વિઝિટ ગોઠવો', hi: 'विज़िट तय करें' }, BTN_CALL],
  },

  // ── 5 Booking ───────────────────────────────────────────────────
  {
    // Replaces swarna_booking_confirmed (that one carried the booking amount).
    // No amounts on WhatsApp.
    name: 'swarna_booking_welcome',
    category: 'UTILITY',
    automation: 'bookingConfirmed',
    purpose: 'Booking recorded in the CRM.',
    body: {
      en: 'Hi {{1}}, congratulations on booking unit *{{2}}* at *{{3}}*! 🎉\n\nWelcome to the Swarna Group family. Our team will guide you through the next steps.',
      gu: 'નમસ્તે {{1}}, *{{3}}* માં યુનિટ *{{2}}* બુક કરવા બદલ અભિનંદન! 🎉\n\nસ્વર્ણ ગ્રુપ પરિવારમાં આપનું સ્વાગત છે. અમારી ટીમ આગળના પગલાંમાં તમને માર્ગદર્શન આપશે.',
      hi: 'नमस्ते {{1}}, *{{3}}* में यूनिट *{{2}}* बुक करने पर बधाई! 🎉\n\nस्वर्ण ग्रुप परिवार में आपका स्वागत है। हमारी टीम आगे के कदमों में आपकी मदद करेगी।',
    },
    variables: [v('Customer name', 'Amit'), v('Unit', 'A-704'), v('Project', 'Swarna Avsar')],
    footer: FOOTER_UTILITY,
  },
  {
    name: 'swarna_booking_documents',
    category: 'UTILITY',
    automation: 'bookingDocuments',
    purpose: 'Documents needed for the agreement.',
    body: {
      en: 'Hi {{1}}, to complete the agreement for unit {{2}} at {{3}}, please share: {{4}}. You can reply here with photos or visit our office.',
      gu: 'નમસ્તે {{1}}, {{3}} ના યુનિટ {{2}} નો કરાર પૂરો કરવા કૃપા કરી મોકલો: {{4}}. તમે અહીં ફોટા મોકલી શકો છો અથવા ઓફિસ આવી શકો છો.',
      hi: 'नमस्ते {{1}}, {{3}} की यूनिट {{2}} का एग्रीमेंट पूरा करने के लिए कृपया भेजें: {{4}}। आप यहाँ फ़ोटो भेज सकते हैं या ऑफ़िस आ सकते हैं।',
    },
    variables: [v('Customer name', 'Amit'), v('Unit', 'A-704'), v('Project', 'Swarna Avsar'), v('Documents', 'PAN card, Aadhaar card, 2 photos')],
    footer: FOOTER_UTILITY,
    buttons: [{ en: 'I have a question', gu: 'મારો એક પ્રશ્ન છે', hi: 'मेरा एक सवाल है' }],
  },
  {
    name: 'swarna_signed_booking_form',
    category: 'UTILITY',
    automation: 'bookingDocuments',
    purpose: 'Manually share the signed booking form PDF with the primary unit holder from Office unit details.',
    header: { type: 'DOCUMENT', referenceTemplate: 'swarna_signed_booking_form' },
    body: {
      en: 'Hello {{1}} 🙏\n\n*Your signed booking form*\n\n📍 *Unit:* {{2}}\n🏢 *Project:* {{3}}\n\nPlease find the signed booking form attached as a PDF. Keep it for your records.\n\nIf you notice any detail that needs correction or have a question, please reply here and our team will help.\n\nThank you for choosing *Swarna Group*!',
      gu: 'નમસ્તે {{1}}જી 🙏\n\n*તમારું સહી કરેલું બુકિંગ ફોર્મ*\n\n📍 *યુનિટ:* {{2}}\n🏢 *પ્રોજેક્ટ:* {{3}}\n\nતમારું સહી કરેલું બુકિંગ ફોર્મ PDF તરીકે જોડ્યું છે. કૃપા કરીને તેને તમારા રેકોર્ડ માટે સાચવી રાખો.\n\nકોઈ વિગતોમાં સુધારો જરૂરી લાગે અથવા પ્રશ્ન હોય તો અહીં જવાબ આપો. અમારી ટીમ મદદ કરશે.\n\n*સ્વર્ણ ગ્રુપ પસંદ કરવા બદલ આભાર!*',
      hi: 'नमस्ते {{1}} जी 🙏\n\n*आपका हस्ताक्षरित बुकिंग फॉर्म*\n\n📍 *यूनिट:* {{2}}\n🏢 *प्रोजेक्ट:* {{3}}\n\nआपका हस्ताक्षरित बुकिंग फॉर्म PDF के रूप में संलग्न है। कृपया इसे अपने रिकॉर्ड के लिए सुरक्षित रखें।\n\nयदि किसी जानकारी में सुधार चाहिए या आपका कोई प्रश्न है, तो यहाँ जवाब दें। हमारी टीम आपकी सहायता करेगी।\n\n*स्वर्ण ग्रुप को चुनने के लिए धन्यवाद!*',
    },
    variables: [v('Customer name', 'Amit'), v('Unit', 'C-703'), v('Project', 'Swarna Avsar')],
  },

  // ── 6 After sale ────────────────────────────────────────────────
  {
    name: 'swarna_payment_due',
    category: 'UTILITY',
    automation: 'paymentDue',
    purpose: 'Instalment due reminder (no amount on WhatsApp; accounts share it).',
    body: {
      en: 'Hi {{1}}, a payment for unit *{{2}}* at *{{3}}* is due on *{{4}}*.\n\nFor the amount and payment details, please contact our accounts team. Please ignore this if already paid.',
      gu: 'નમસ્તે {{1}}, *{{3}}* ના યુનિટ *{{2}}* માટેની ચુકવણી *{{4}}* ના રોજ બાકી છે.\n\nરકમ અને ચુકવણીની વિગતો માટે અમારી એકાઉન્ટ્સ ટીમનો સંપર્ક કરો. જો ચૂકવી દીધું હોય તો આ મેસેજ અવગણો.',
      hi: 'नमस्ते {{1}}, *{{3}}* की यूनिट *{{2}}* का भुगतान *{{4}}* को देय है।\n\nराशि और भुगतान की जानकारी के लिए हमारी अकाउंट्स टीम से संपर्क करें। अगर भुगतान हो चुका है तो इसे अनदेखा करें।',
    },
    variables: [v('Customer name', 'Amit'), v('Unit', 'A-704'), v('Project', 'Swarna Avsar'), v('Due date', '25 Oct 2026')],
    footer: FOOTER_UTILITY,
    buttons: [{ en: 'Already paid', gu: 'ચૂકવી દીધું', hi: 'भुगतान हो गया' }, { en: 'Talk to accounts', gu: 'એકાઉન્ટ્સ સાથે વાત', hi: 'अकाउंट्स से बात' }],
  },
  {
    name: 'swarna_unit_payment_details',
    draftRevision: 1,
    category: 'UTILITY',
    automation: 'paymentReceived',
    purpose: 'Manually send the unit-specific payment details PDF to the primary unit holder from Office payment transactions.',
    header: { type: 'DOCUMENT', referenceTemplate: 'swarna_unit_payment_details' },
    body: {
      en: 'Hello {{1}} 🙏\n\n*Payment details for your unit*\n\n📍 *Unit:* {{2}}\n🏢 *Project:* {{3}}\n\nPlease find your payment details in the attached PDF.\n\nIf you need any help, reply to this message or contact our accounts team.\n\nThank you for choosing *Swarna Group*! 🙏',
      gu: 'નમસ્તે {{1}}જી 🙏\n\n*તમારી યુનિટની ચુકવણીની વિગતો*\n\n📍 *યુનિટ:* {{2}}\n🏢 *પ્રોજેક્ટ:* {{3}}\n\nતમારી ચુકવણીની વિગતો જોડાયેલ PDFમાં આપેલી છે.\n\nકોઈ મદદ માટે આ સંદેશનો જવાબ આપો અથવા અમારી એકાઉન્ટ્સ ટીમનો સંપર્ક કરો.\n\n*સ્વર્ણ ગ્રુપ પસંદ કરવા બદલ આભાર!* 🙏',
      hi: 'नमस्ते {{1}} जी 🙏\n\n*आपकी यूनिट के भुगतान का विवरण*\n\n📍 *यूनिट:* {{2}}\n🏢 *प्रोजेक्ट:* {{3}}\n\nआपके भुगतान का विवरण संलग्न PDF में दिया गया है।\n\nकिसी सहायता के लिए इस संदेश का जवाब दें या हमारी अकाउंट्स टीम से संपर्क करें।\n\n*स्वर्ण ग्रुप को चुनने के लिए धन्यवाद!* 🙏',
    },
    variables: [v('Customer name', 'Amit'), v('Unit', 'C-704'), v('Project', 'Swarna Avsar')],
  },
  {
    name: 'swarna_property_payment_receipt',
    category: 'UTILITY',
    automation: 'paymentReceived',
    purpose: 'Confirmation for a regular property payment. Text only; uploaded bank evidence remains internal.',
    body: {
      en: 'Hi {{1}}, we have received *₹{{2}}* toward unit *{{3}}* at *{{4}}* on {{5}}.\n\nReceipt no. *{{6}}*. Thank you! 🙏',
      gu: 'નમસ્તે {{1}}, *{{4}}* ના યુનિટ *{{3}}* માટે {{5}} ના રોજ *₹{{2}}* ની તમારી પ્રોપર્ટી પેમેન્ટ અમને સફળતાપૂર્વક મળી ગઈ છે.\n\nરસીદ નં. *{{6}}*. આ ચુકવણી નોંધાઈ ગઈ છે. સ્વર્ણ ગ્રુપ પર વિશ્વાસ રાખવા બદલ આભાર! 🙏',
      hi: 'नमस्ते {{1}}, *{{4}}* की यूनिट *{{3}}* के लिए {{5}} को *₹{{2}}* का आपका प्रॉपर्टी भुगतान हमें सफलतापूर्वक प्राप्त हो गया है।\n\nरसीद नं. *{{6}}*। यह भुगतान दर्ज कर लिया गया है। स्वर्ण ग्रुप पर विश्वास करने के लिए धन्यवाद! 🙏',
    },
    variables: [v('Customer name', 'Amit'), v('Amount', '150000'), v('Unit', 'A-704'), v('Project', 'Swarna Avsar'), v('Date', '15 Oct 2026'), v('Receipt no.', 'REC/SA/12')],
    footer: FOOTER_UTILITY,
  },
  {
    name: 'swarna_gst_payment_receipt',
    category: 'UTILITY',
    automation: 'paymentReceived',
    purpose: 'Confirmation for a GST payment. This is a payment acknowledgement, not a tax invoice.',
    body: {
      en: 'Hi {{1}}, we have received *₹{{2}}* as GST payment for unit *{{3}}* at *{{4}}* on {{5}}.\n\nReceipt no. *{{6}}*. This message is a payment acknowledgement, not a tax invoice. Thank you! 🙏',
      gu: 'નમસ્તે {{1}}, *{{4}}* ના યુનિટ *{{3}}* માટે {{5}} ના રોજ GST ચુકવણી તરીકે *₹{{2}}* મળ્યા છે.\n\nરસીદ નં. *{{6}}*. આ સંદેશ ચુકવણીની પહોંચ છે, ટેક્સ ઇન્વૉઇસ નથી. આભાર! 🙏',
      hi: 'नमस्ते {{1}}, *{{4}}* की यूनिट *{{3}}* के लिए {{5}} को GST भुगतान के रूप में *₹{{2}}* प्राप्त हुए।\n\nरसीद नं. *{{6}}*। यह संदेश भुगतान की पुष्टि है, टैक्स इनवॉइस नहीं। धन्यवाद! 🙏',
    },
    variables: [v('Customer name', 'Amit'), v('GST amount', '7500'), v('Unit', 'A-704'), v('Project', 'Swarna Avsar'), v('Date', '15 Oct 2026'), v('Receipt no.', 'GST/SA/5')],
    footer: FOOTER_UTILITY,
  },
  {
    name: 'swarna_construction_update',
    category: 'UTILITY',
    automation: 'constructionUpdate',
    purpose: 'Progress update to buyers of a project.',
    body: {
      en: 'Hi {{1}}, construction update for {{2}}: {{3}}. Expected possession: {{4}}. Thank you for your trust in us.',
      gu: 'નમસ્તે {{1}}, {{2}} નું બાંધકામ અપડેટ: {{3}}. અપેક્ષિત પઝેશન: {{4}}. અમારા પર વિશ્વાસ રાખવા બદલ આભાર.',
      hi: 'नमस्ते {{1}}, {{2}} का निर्माण अपडेट: {{3}}। संभावित पज़ेशन: {{4}}। हम पर भरोसा करने के लिए धन्यवाद।',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Avsar'), v('Update', 'slab work of the 9th floor is complete'), v('Possession', 'December 2026')],
    footer: FOOTER_UTILITY,
  },
  {
    name: 'swarna_possession_ready',
    category: 'UTILITY',
    automation: 'possession',
    purpose: 'Unit ready for handover.',
    body: {
      en: 'Hi {{1}}, great news! Unit {{2}} at {{3}} is ready for possession from {{4}}. {{5}} will call you to schedule the handover.',
      gu: 'નમસ્તે {{1}}, ખુશખબર! {{3}} નું યુનિટ {{2}} {{4}} થી પઝેશન માટે તૈયાર છે. હેન્ડઓવરનો સમય નક્કી કરવા {{5}} તમને કૉલ કરશે.',
      hi: 'नमस्ते {{1}}, खुशखबरी! {{3}} की यूनिट {{2}} {{4}} से पज़ेशन के लिए तैयार है। हैंडओवर का समय तय करने के लिए {{5}} आपको कॉल करेंगे।',
    },
    variables: [v('Customer name', 'Amit'), v('Unit', 'A-704'), v('Project', 'Swarna Avsar'), v('Date', '1 Dec 2026'), v('Executive', 'Rahul')],
    footer: FOOTER_UTILITY,
    buttons: [{ en: 'Schedule handover', gu: 'હેન્ડઓવર નક્કી કરો', hi: 'हैंडओवर तय करें' }, BTN_CALL],
  },

  // ── Marketing ───────────────────────────────────────────────────
  {
    name: 'swarna_new_launch',
    category: 'MARKETING',
    automation: 'newLaunch',
    purpose: 'New project or phase launch to interested leads.',
    body: {
      en: 'Hi {{1}}, Swarna Group is launching *{{2}}* at {{3}}. {{4}}.\n\nFor the latest pricing and availability, we’d be happy to show you the available options during a site visit.',
      gu: 'નમસ્તે {{1}}, સ્વર્ણ ગ્રુપ {{3}} માં *{{2}}* લોન્ચ કરી રહ્યું છે. {{4}}.\n\nતાજા ભાવ અને ઉપલબ્ધતા માટે, સાઇટ વિઝિટ દરમિયાન અમે તમને ઉપલબ્ધ વિકલ્પો બતાવીશું.',
      hi: 'नमस्ते {{1}}, स्वर्ण ग्रुप {{3}} में *{{2}}* लॉन्च कर रहा है। {{4}}।\n\nनई कीमत और उपलब्धता के लिए, साइट विज़िट के दौरान हम आपको उपलब्ध विकल्प दिखाएँगे।',
    },
    variables: [v('Customer name', 'Amit'), v('Project', 'Swarna Lagom Phase 2'), v('Area', 'Vanakla, Surat'), v('Highlight', '2 BHK homes and retail shops')],
    footer: FOOTER_MARKETING,
    buttons: [{ en: 'Send details', gu: 'વિગતો મોકલો', hi: 'जानकारी भेजें' }, BTN_VISIT, BTN_STOP],
  },
  {
    name: 'swarna_birthday',
    category: 'MARKETING',
    automation: 'birthday',
    purpose: 'Birthday wish (replaces the current birthday message after testing).',
    body: {
      en: 'Happy birthday, {{1}}! Wishing you and your family a wonderful year ahead from all of us at Swarna Group.',
      gu: 'જન્મદિવસની હાર્દિક શુભેચ્છા, {{1}}! સ્વર્ણ ગ્રુપ પરિવાર તરફથી તમને અને તમારા પરિવારને આવનારું વર્ષ શુભ રહે.',
      hi: 'जन्मदिन की हार्दिक शुभकामनाएँ, {{1}}! स्वर्ण ग्रुप परिवार की ओर से आपको और आपके परिवार को आने वाला साल शुभ हो।',
    },
    variables: [v('Customer name', 'Amit')],
    footer: FOOTER_MARKETING,
    buttons: [BTN_STOP],
  },
  {
    name: 'swarna_festival_greeting',
    category: 'MARKETING',
    automation: 'festival',
    purpose: 'Festival greeting to customers and warm leads.',
    body: {
      en: 'Hi {{1}}, Swarna Group wishes you and your family a very happy {{2}}! {{3}}\nWarm wishes, Team Swarna',
      gu: 'નમસ્તે {{1}}, સ્વર્ણ ગ્રુપ તરફથી તમને અને તમારા પરિવારને {{2}} ની હાર્દિક શુભેચ્છા! {{3}}\nશુભેચ્છાઓ સાથે, ટીમ સ્વર્ણ',
      hi: 'नमस्ते {{1}}, स्वर्ण ग्रुप की ओर से आपको और आपके परिवार को {{2}} की हार्दिक शुभकामनाएँ! {{3}}\nशुभकामनाओं सहित, टीम स्वर्ण',
    },
    variables: [v('Customer name', 'Amit'), v('Festival', 'Diwali'), v('Message', 'May your new home bring you joy.')],
    footer: FOOTER_MARKETING,
    buttons: [BTN_STOP],
  },
  {
    name: 'swarna_collection_ready',
    category: 'MARKETING',
    automation: 'collectionReady',
    purpose: 'Tell selected recipients an item is ready to collect, with a directions button.',
    body: {
      en: 'Hello {{1}} 👋\n\nYou can now collect *{{2}}* from us.\n\nPlease visit us on *{{3}}*, between *{{4}}*.\n📍 Collection point: {{5}}. Tap *Get directions* below for the map.\n\nIf this time does not work for you, please reply to this message.\n\nWarm regards,\nSwarna Group',
      gu: 'નમસ્તે {{1}} 👋\n\nહવે તમે અમારી પાસેથી *{{2}}* મેળવી શકો છો.\n\nકૃપા કરીને *{{3}}*ના રોજ *{{4}}* દરમિયાન મુલાકાત લો.\n📍 મેળવવાનું સ્થળ: {{5}}. નકશો જોવા નીચે *દિશા જુઓ* દબાવો.\n\nઆ સમય અનુકૂળ ન હોય તો આ મેસેજનો જવાબ આપો.\n\nશુભેચ્છાઓ સાથે,\nસ્વર્ણ ગ્રુપ',
      hi: 'नमस्ते {{1}} 👋\n\nअब आप हमसे *{{2}}* प्राप्त कर सकते हैं।\n\nकृपया *{{3}}* को *{{4}}* के बीच आएँ।\n📍 प्राप्त करने का स्थान: {{5}}। नक्शे के लिए नीचे *दिशा देखें* दबाएँ।\n\nयदि यह समय सुविधाजनक नहीं है, तो इस संदेश का जवाब दें।\n\nसादर,\nस्वर्ण ग्रुप',
    },
    variables: [v('Recipient name', 'Amit'), v('Item', 'Diwali gift'), v('Collection date', '10 November 2026'), v('Time window', '10:00 am–6:00 pm'), v('Collection address', 'Swarna Group office, Surat')],
    footer: FOOTER_MARKETING,
    buttons: [
      { en: 'Get directions', gu: 'દિશા જુઓ', hi: 'दिशा देखें' },
      BTN_STOP,
    ],
    urlButton: {
      template: 'https://maps.google.com/maps?q={{1}}',
      sample: 'https://maps.google.com/maps?q=21.1702%2C72.8311',
      note: 'Opens the pickup location entered for this collection send.',
    },
  },
];

/** Meta `components` payload for one language of a draft. */
export const templateComponents = (draft: TemplateDraft, lang: AutoReplyLang) => {
  const components: Array<Record<string, unknown>> = [];
  const usedVariableIndexes = Array.from(
    new Set(Array.from(draft.body[lang].matchAll(/\{\{(\d+)\}\}/g)).map((match) => Number(match[1])))
  ).sort((left, right) => left - right);
  if (draft.header?.type === 'LOCATION') components.push({ type: 'HEADER', format: 'LOCATION' });
  if (draft.header?.type === 'TEXT') components.push({ type: 'HEADER', format: 'TEXT', text: draft.header.text[lang] });
  if (draft.header?.type === 'DOCUMENT') components.push({ type: 'HEADER', format: 'DOCUMENT' });
  components.push({
    type: 'BODY',
    text: draft.body[lang],
    ...(usedVariableIndexes.length
      ? { example: { body_text: [usedVariableIndexes.map((index) => draft.variables[index - 1]?.example || '')] } }
      : {}),
  });
  if (draft.footer) components.push({ type: 'FOOTER', text: draft.footer[lang] });
  if (draft.buttons?.length) {
    components.push({ type: 'BUTTONS', buttons: draft.buttons.map((b, i) =>
      draft.urlButton && i === 0 ? {
        type: 'URL', text: b[lang],
        url: draft.urlButton.template,
        example: [draft.urlButton.sample],
      } : { type: 'QUICK_REPLY', text: b[lang] }) });
  }
  return components;
};

/** Body with example values filled in, for previews. */
export const templatePreviewText = (draft: TemplateDraft, lang: AutoReplyLang) =>
  draft.body[lang].replace(/\{\{(\d+)\}\}/g, (match, n) => draft.variables[Number(n) - 1]?.example ?? match);

/** Local checks against Meta's template rules before submitting. */
export const templateIssues = (draft: TemplateDraft, lang: AutoReplyLang): string[] => {
  const issues: string[] = [];
  const body = draft.body[lang].trim();
  if (body.length > 1024) issues.push('Body is longer than 1024 characters.');
  if (/^\{\{\d+\}\}/.test(body) || /\{\{\d+\}\}[.!?]?$/.test(body)) issues.push('Body must not start or end with a variable.');
  const used = Array.from(new Set(Array.from(body.matchAll(/\{\{(\d+)\}\}/g)).map((m) => Number(m[1])))).sort((a, b) => a - b);
  if (used.some((n, i) => n !== i + 1)) issues.push('Variables must be numbered 1, 2, 3 … without gaps.');
  if (draft.footer && draft.footer[lang].length > 60) issues.push('Footer is longer than 60 characters.');
  draft.buttons?.forEach((b) => {
    if (b[lang].length > 25) issues.push(`Button "${b[lang]}" is longer than 25 characters.`);
  });
  return issues;
};
